import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, Mail, ArrowRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';

const navLinks = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  {
    name: 'Projects',
    to: '/projects',
    children: [
      { name: 'Current Projects', to: '/projects?category=current', category: 'current' },
      { name: 'Project Completed', to: '/projects?category=completed', category: 'completed' },
    ],
  },
  { name: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopProjectsOpen, setIsDesktopProjectsOpen] = useState(false);
  const [isMobileProjectsOpen, setIsMobileProjectsOpen] = useState(false);
  const location = useLocation();
  const activeProjectCategory = new URLSearchParams(location.search).get('category');
  const isProjectsPage = location.pathname === '/projects';
  const logoSrc = `${process.env.PUBLIC_URL}/asserts/logo-transparent.png`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDesktopProjectsOpen(false);
    setIsMobileProjectsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isMobileMenuOpen && isProjectsPage) {
      setIsMobileProjectsOpen(true);
    }
  }, [isMobileMenuOpen, isProjectsPage]);

  return (
    <nav className="fixed inset-x-0 top-0 z-50">
      <div
        className={cn(
          'transition-all duration-500',
          isScrolled ? '' : 'bg-transparent'
        )}
      >
        <div className="flex w-full items-center gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.35rem]">
              <img
                src={logoSrc}
                alt="Suraksha Builders logo"
                className="h-20 w-20 object-contain"
              />
            </div>
           
          </Link>

          <div className="hidden flex-1 justify-center lg:flex">
            <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.35)] backdrop-blur">
              {navLinks.map((link) => {
                if (!link.children) {
                  return (
                    <NavLink
                      key={link.name}
                      to={link.to}
                      end={link.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.26em] transition-all duration-300',
                          isActive
                            ? 'bg-white/12 text-secondary shadow-[0_12px_24px_rgba(0,0,0,0.4)]'
                            : 'text-secondary/60 hover:bg-white/8 hover:text-secondary'
                        )
                      }
                    >
                      {link.name}
                    </NavLink>
                  );
                }

                const isProjectsActive = isProjectsPage || isDesktopProjectsOpen;

                return (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setIsDesktopProjectsOpen(true)}
                    onMouseLeave={() => setIsDesktopProjectsOpen(false)}
                  >
                    <div
                      className={cn(
                        'flex items-center rounded-full transition-all duration-300',
                        isProjectsActive
                          ? 'bg-white/12 text-secondary shadow-[0_12px_24px_rgba(0,0,0,0.4)]'
                          : 'text-secondary/60 hover:bg-white/8 hover:text-secondary'
                      )}
                    >
                      <Link
                        to={link.to}
                        className="rounded-full py-3 pl-5 pr-2 text-[11px] font-semibold uppercase tracking-[0.26em]"
                      >
                        {link.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setIsDesktopProjectsOpen((open) => !open)}
                        className="mr-1 flex h-9 w-9 items-center justify-center rounded-full"
                        aria-label="Toggle Projects submenu"
                        aria-expanded={isDesktopProjectsOpen}
                      >
                        <ChevronDown
                          size={16}
                          className={cn('transition-transform duration-300', isDesktopProjectsOpen && 'rotate-180')}
                        />
                      </button>
                    </div>

                    <AnimatePresence>
                      {isDesktopProjectsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 12 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 top-full z-20 pt-3"
                        >
                          <div className="min-w-[240px] rounded-[1.5rem] border border-white/10 bg-primary/90 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl">
                            {link.children.map((child) => {
                              const isChildActive = isProjectsPage && activeProjectCategory === child.category;

                              return (
                                <Link
                                  key={child.name}
                                  to={child.to}
                                  className={cn(
                                    'flex items-center justify-between rounded-[1.1rem] px-4 py-3 text-sm font-semibold transition-all duration-300',
                                    isChildActive
                                      ? 'bg-white/12 text-secondary'
                                      : 'text-secondary/70 hover:bg-white/8 hover:text-secondary'
                                  )}
                                >
                                  {child.name}
                                  <ArrowRight size={15} />
                                </Link>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <a
              href="tel:+919535274780"
              className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-secondary/80 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 text-accent">
                <Phone size={16} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-secondary/40">Studio Line</p>
                <p className="text-sm font-semibold text-secondary">+91 95352 74780</p>
              </div>
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 rounded-full bg-accent px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light hover:text-primary"
            >
              Book a Visit
              <ArrowRight size={15} />
            </Link>
          </div>

          <button
            className="ml-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/8 text-secondary shadow-[0_10px_30px_rgba(0,0,0,0.45)] lg:hidden"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="border-b border-white/10 bg-primary/95 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl lg:hidden"
          >
            <div className="w-full px-4 pb-6 sm:px-6 lg:px-8">
              <div className="soft-panel mb-5 flex items-center gap-4 p-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.25rem]">
                  <img
                    src={logoSrc}
                    alt="Suraksha Builders logo"
                    className="h-11 w-11 object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-2xl font-bold leading-tight gold-text-gradient">
                    Suraksha Builders &amp; Developers
                  </p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-accent/80">
                    Land crafted with hotel calm.
                  </p>
                </div>
              </div>

              <div className="grid gap-3">
                {navLinks.map((link, index) => {
                  if (!link.children) {
                    return (
                      <motion.div
                        key={link.name}
                        initial={{ opacity: 0, x: 14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <NavLink
                          to={link.to}
                          end={link.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between rounded-[1.5rem] border px-5 py-4',
                          isActive
                            ? 'border-white/10 bg-white/10 text-secondary'
                            : 'border-white/10 bg-white/5 text-secondary'
                        )
                      }
                    >
                          <span className="font-serif text-2xl">{link.name}</span>
                          <ArrowRight size={18} />
                        </NavLink>
                      </motion.div>
                    );
                  }

                  const isProjectsActive = isProjectsPage || isMobileProjectsOpen;

                  return (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 14 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div
                        className={cn(
                          'overflow-hidden rounded-[1.5rem] border',
                          isProjectsActive
                            ? 'border-white/10 bg-white/10 text-secondary'
                            : 'border-white/10 bg-white/5 text-secondary'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <Link to={link.to} className="flex-1 px-5 py-4 font-serif text-2xl">
                            {link.name}
                          </Link>
                          <button
                            type="button"
                            onClick={() => setIsMobileProjectsOpen((open) => !open)}
                            className="mr-3 flex h-10 w-10 items-center justify-center rounded-full"
                            aria-label="Toggle Projects submenu"
                            aria-expanded={isMobileProjectsOpen}
                          >
                            <ChevronDown
                              size={18}
                              className={cn('transition-transform duration-300', isMobileProjectsOpen && 'rotate-180')}
                            />
                          </button>
                        </div>

                        <AnimatePresence>
                          {isMobileProjectsOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.24 }}
                              className="border-t border-white/10"
                            >
                              <div className="grid gap-2 p-3">
                                {link.children.map((child) => {
                                  const isChildActive = isProjectsPage && activeProjectCategory === child.category;

                                  return (
                                    <Link
                                      key={child.name}
                                      to={child.to}
                                      className={cn(
                                        'flex items-center justify-between rounded-[1.1rem] px-4 py-3 text-sm font-semibold transition-all duration-300',
                                        isChildActive
                                          ? 'bg-white/12 text-secondary'
                                          : 'bg-white/8 text-secondary/80 hover:bg-white/15'
                                      )}
                                    >
                                      {child.name}
                                      <ArrowRight size={15} />
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <a href="tel:+919535274780" className="soft-panel p-4">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Phone size={18} />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.26em] text-secondary/40">Studio Line</p>
                  <p className="mt-2 text-base font-semibold text-secondary">+91 95352 74780</p>
                </a>
                <a href="mailto:surakshabd2023@gmail.com" className="soft-panel p-4">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
                    <Mail size={18} />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.26em] text-secondary/40">Private Mail</p>
                  <p className="mt-2 text-base font-semibold text-secondary">surakshabd2023@gmail.com</p>
                </a>
              </div>

              <Link
                to="/contact"
                className="mt-5 inline-flex w-full items-center justify-center gap-3 rounded-full bg-accent px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-secondary"
              >
                Request a Callback
                <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
